import { useState, useEffect } from "react";
import { X, Cookie, Settings } from "lucide-react";
import { useCookieConsent } from "../hooks/use-cookie-consent";
import { Button } from "./ui/button";
import { CookiePreferencesDialog } from "./CookiePreferencesDialog";
import { Link } from "react-router-dom";

export function CookieConsentBanner() {
  const { hasConsented, acceptAll, rejectAll } = useCookieConsent();
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Show banner after a short delay if user hasn't consented
    if (!hasConsented) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasConsented]);

  const handleAcceptAll = () => {
    acceptAll();
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowPreferences(true);
  };

  const handlePreferencesSaved = () => {
    setShowPreferences(false);
    setIsVisible(false);
  };

  if (!isVisible || hasConsented) {
    return null;
  }

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom duration-300"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent banner"
      >
        <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-2xl">
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="flex-shrink-0 mt-1">
                <Cookie className="h-6 w-6 text-primary" aria-hidden="true" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Cookie Preferences
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  We use cookies to enhance your browsing experience, analyze
                  site traffic, and personalize content. Essential cookies are
                  required for the site to function. You can customize your
                  preferences or accept all cookies.{" "}
                  <Link
                    to="/cookies"
                    className="text-primary hover:underline font-medium"
                  >
                    Learn more about our Cookie Policy
                  </Link>
                </p>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    Accept All Cookies
                  </Button>
                  <Button
                    onClick={handleRejectAll}
                    variant="outline"
                    className="border-gray-300 dark:border-gray-600"
                  >
                    Reject Non-Essential
                  </Button>
                  <Button
                    onClick={handleCustomize}
                    variant="ghost"
                    className="gap-2"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                    Customize
                  </Button>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={handleRejectAll}
                className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Close and reject non-essential cookies"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Dialog */}
      <CookiePreferencesDialog
        open={showPreferences}
        onOpenChange={setShowPreferences}
        onSave={handlePreferencesSaved}
      />
    </>
  );
}
